import i18next, { type i18n as I18n } from "i18next";

/**
 * i18n (CAP-9, ADR 0001). Every user-visible string is a key; English is
 * the only locale at launch. Locale files are discovered from
 * `packages/flows/locales/*.json`, so adding a language needs no code change.
 * Tenants may override keys from their brand file, except `legal.*`.
 * Interpolated values must already be formatted with `@kovernow/flows/format`.
 */

type Resource = Record<string, unknown>;

const modules = import.meta.glob<Resource>("../../locales/*.json", {
  eager: true,
  import: "default",
});

/** Locale files keyed by language (file basename), e.g. { en: {...} }. */
export const LOCALE_RESOURCES: Record<string, Resource> = Object.fromEntries(
  Object.entries(modules).map(([file, res]) => [file.replace(/^.*\/([^/]+)\.json$/, "$1"), res]),
);

export const DEFAULT_LANGUAGE = "en";
export const LEGAL_KEY_PREFIX = "legal.";

/** Dotted leaf keys of a nested resource, e.g. ["auth.signIn.title", …]. */
export function flattenKeys(obj: Resource, prefix = ""): string[] {
  return Object.entries(obj).flatMap(([k, v]) =>
    typeof v === "object" && v !== null && !Array.isArray(v)
      ? flattenKeys(v as Resource, `${prefix}${k}.`)
      : [`${prefix}${k}`],
  );
}

function unflatten(flat: Record<string, string>): Resource {
  const out: Resource = {};
  for (const [path, value] of Object.entries(flat)) {
    const segs = path.split(".");
    let node = out;
    segs.forEach((seg, i) => {
      if (i === segs.length - 1) node[seg] = value;
      else node = (node[seg] ??= {}) as Resource;
    });
  }
  return out;
}

/** Tenant overrides minus legal copy and keys that don't exist in English. */
export function allowedOverrides(overrides: Record<string, string> | undefined): Record<string, string> {
  if (!overrides) return {};
  const known = new Set(flattenKeys(LOCALE_RESOURCES[DEFAULT_LANGUAGE] ?? {}));
  return Object.fromEntries(
    Object.entries(overrides).filter(
      ([k, v]) => typeof v === "string" && !k.startsWith(LEGAL_KEY_PREFIX) && known.has(k),
    ),
  );
}

export interface CreateI18nOptions {
  /** Tenant locale, e.g. en-SG. Falls back to its language, then English. */
  locale: string;
  overrides?: Record<string, string>;
  /** Called for any key that is missing in every locale (dev: throw/log). */
  onMissingKey?: (key: string) => void;
}

export async function createI18n(options: CreateI18nOptions): Promise<I18n> {
  const instance = i18next.createInstance();
  const resources = Object.fromEntries(
    Object.entries(LOCALE_RESOURCES).map(([lng, res]) => [lng, { translation: res }]),
  );
  await instance.init({
    lng: options.locale,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: false,
    resources,
    interpolation: { escapeValue: false },
    returnNull: false,
    showSupportNotice: false,
    saveMissing: !!options.onMissingKey,
    missingKeyHandler: options.onMissingKey
      ? (_lngs, _ns, key) => options.onMissingKey?.(key)
      : undefined,
  });
  const allowed = allowedOverrides(options.overrides);
  if (Object.keys(allowed).length) {
    const lang = instance.resolvedLanguage ?? DEFAULT_LANGUAGE;
    instance.addResourceBundle(lang, "translation", unflatten(allowed), true, true);
  }
  return instance;
}

export type { I18n };
